/**
 * Option ECharts du radar de matching fit-culturel — module PUR.
 *
 * Extrait de `FitCulturelMatchingChart.tsx`, qui n'en est plus que l'enveloppe
 * React. La raison : le rapport PDF rend le MÊME graphe côté serveur, via
 * `generate-echarts-image`, et un graphe redessiné à la main pour le document
 * finirait par diverger de l'écran. Un seul constructeur, deux rendus.
 *
 * Pas d'import React ni de DOM au chargement : ce fichier doit pouvoir être
 * importé par du code Node (et, à terme, par `convex/reports.ts`), exactement
 * comme `buildPamiPolarOption` l'est déjà pour le PAMI.
 */

/**
 * Un item du socle confronté au niveau attendu par le référentiel. Forme
 * MINIMALE et locale : ce paquet ne dépend pas de Convex, et
 * `convex/referentiel/rapport.ts` (`matchingParItem`) produit exactement ces
 * champs.
 *
 * Les deux niveaux sont des scores de pyramide : `nbNiveaux` = haut de pyramide
 * (le plus attendu / le plus valorisé), 1 = bas.
 */
export type LigneMatchingFitCulturel = {
  code: string;
  label: string;
  categorie: string | null;
  niveauEntreprise: number;
  niveauCandidat: number;
  ecart: number;
  statut: "dans-la-cible" | "sous-la-cible" | "au-dessus-de-la-cible";
};

/**
 * Couleur du point candidat : vert dans l'anneau attendu, bleu partout ailleurs.
 *
 * DEUX couleurs et non trois, contrairement au radar PAMI : le sens de l'écart
 * ne se code plus par la teinte, il se LIT à la position du point par rapport à
 * son anneau. En tri à distribution forcée, un item placé trop haut n'est pas
 * « mieux » ni « pire » qu'un item placé trop bas — un rouge d'un côté et un
 * bleu de l'autre suggéreraient une gravité que le fit culturel ne mesure pas.
 *
 * Jamais la couleur seule : l'infobulle écrit aussi le statut et l'écart.
 */
export const COULEUR_STATUT: Record<
  LigneMatchingFitCulturel["statut"],
  string
> = {
  "dans-la-cible": "#16a34a",
  "sous-la-cible": "#2563eb",
  "au-dessus-de-la-cible": "#2563eb",
};

export const LIBELLE_STATUT: Record<
  LigneMatchingFitCulturel["statut"],
  string
> = {
  "dans-la-cible": "Au niveau attendu",
  "sous-la-cible": "Sous le niveau attendu",
  "au-dessus-de-la-cible": "Au-dessus du niveau attendu",
};

/**
 * Repli hexadécimal de la couleur `primary` du design system — l'équivalent de
 * `oklch(0.5338 0.2503 301.37)` (`packages/tailwind-config`). Il sert au rendu
 * sans CSS chargé, donc à TOUT rendu serveur ; en navigateur c'est le token qui
 * gagne, thème sombre compris.
 */
export const PRIMARY_FALLBACK = "#8a2be2";

/** Teinte UNIQUE des crans du référentiel, lue sur `--primary` pour suivre le
 *  thème. Une seule teinte parce que la couleur ne code rien ici : le niveau
 *  attendu se lit à la distance au centre. */
export function couleurCran(): string {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return PRIMARY_FALLBACK;
  }
  const valeur = getComputedStyle(document.documentElement)
    .getPropertyValue("--primary")
    .trim();
  return valeur || PRIMARY_FALLBACK;
}

/** Séparateur entre deux items : pointillé léger et UNIFORME. La grille sépare,
 *  elle ne hiérarchise pas. */
const TRAIT_DIMENSION = "rgba(0,0,0,0.12)";

/**
 * Rayon auquel se dessine un niveau : le MILIEU de sa bande, pas sa frontière.
 *
 * L'anneau du niveau N occupe `[N−1, N]` ; tracer le point du candidat à `N` le
 * poserait pile sur le bord extérieur, à cheval sur la bande voisine — illisible,
 * et trompeur quand le point du candidat et l'anneau attendu se touchent sans
 * coïncider. Décalé d'un demi-cran, un point tombe au centre de sa bande : il est
 * dans l'anneau violet ou clairement à côté.
 *
 * Décale le POINT, pas l'échelle : l'axe reste gradué 0→nbNiveaux et ses cercles
 * marquent les frontières entre crans. Les niveaux affichés (infobulle, noms
 * d'axes) restent entiers.
 */
const rayonDuNiveau = (niveau: number) => niveau - 0.5;

/**
 * Longueur maximale d'une ligne d'étiquette, en caractères.
 *
 * 11 et non 14 : à 23 items, deux étiquettes voisines du bas du cercle sont
 * presque côte à côte, et la largeur de leur texte est ce qui les fait se
 * chevaucher — « Considération des personnes » passait sur « Responsabilité
 * sociale ». Couper plus court les empile en hauteur, où il reste de la place,
 * plutôt qu'en largeur, où il n'y en a plus.
 */
const LARGEUR_NOM = 11;

/** Diamètre UNIQUE des points candidat : le fit culturel n'a pas de pondération
 *  — tous les items pèsent pareil, la taille ne doit donc rien laisser croire. */
const TAILLE_POINT = 9;

/**
 * Coupe un nom trop long aux espaces, en AUTANT de lignes que nécessaire.
 *
 * La version précédente n'en faisait que deux, ce qui laissait « Opportunités /
 * de développement professionnel » avec une seconde ligne de trente signes :
 * débordement hors du cadre à l'impression, chevauchement à l'écran. Un mot
 * plus long que la limite reste sur sa ligne — mieux vaut dépasser un peu que
 * couper un mot en deux.
 */
export function couperNom(nom: string, longueurMax = 14): string {
  if (nom.length <= longueurMax) return nom;
  const lignes: string[] = [];
  let courante = "";
  for (const mot of nom.split(" ")) {
    const suivant = (courante + " " + mot).trim();
    if (courante && suivant.length > longueurMax) {
      lignes.push(courante);
      courante = mot;
    } else {
      courante = suivant;
    }
  }
  if (courante) lignes.push(courante);
  return lignes.join("\n");
}

/**
 * Éloigne une étiquette sur deux du cercle, d'une ligne.
 *
 * À 23 items, deux étiquettes voisines ne sont séparées que d'un seizième de
 * tour : en bas de la figure, leurs blocs de texte se recouvraient
 * (« Considération des personnes » par-dessus « Responsabilité sociale »).
 * Les raccourcir ne suffit pas — un mot ne se coupe pas, et « Considération »
 * fait déjà treize signes.
 *
 * Une ligne vide ajoutée du côté du cercle pousse donc l'étiquette vers
 * l'extérieur, et il suffit de le faire une fois sur deux pour que deux
 * voisines ne soient plus jamais à la même distance. Le côté où l'ajouter
 * dépend de la moitié où tombe l'item : en haut, la ligne vide va sous le
 * texte ; en bas, au-dessus.
 */
function ecarterNom(nom: string, index: number, total: number): string {
  if (total === 0) return nom;
  // Même géométrie que la figure : départ à 90°, sens trigonométrique.
  const angle = ((90 + (360 * index) / total) * Math.PI) / 180;
  const vertical = Math.sin(angle);

  // Seules les étiquettes tout en haut et tout en bas du cercle se gênent :
  // là, deux voisines sont posées CÔTE À CÔTE et leurs textes se recouvrent.
  // Ailleurs elles s'empilent déjà verticalement et ont la place — les écarter
  // ne ferait que créer un chevauchement là où il n'y en avait pas.
  if (Math.abs(vertical) < 0.9 || index % 2 === 0) return nom;

  // Lignes ajoutées DU CÔTÉ DU CENTRE : le bloc grandit de ce côté, donc
  // l'étiquette s'éloigne du cercle.
  //
  // Elles portent une espace insécable, car ECharts élague les lignes vraiment
  // vides. Et il en faut QUATRE pour un décalage d'un interligne : le bloc
  // étant centré sur son ancre, chaque ligne ajoutée ne déplace le texte que
  // d'une demi-ligne.
  const remplissage = Array.from({ length: 4 }, () => "\u00a0");
  return vertical > 0
    ? [nom, ...remplissage].join("\n")
    : [...remplissage, nom].join("\n");
}

export type OptionMatchingParams = {
  nbNiveaux: number;
  titre?: string;
  /**
   * Rendu destiné à une image fixe : ni animation, ni boîte à outils, ni
   * infobulle — rien de tout cela ne survit à un PNG, et l'animation fait
   * capturer le graphe à mi-course.
   */
  printMode?: boolean;
  /**
   * L'option part en JSON vers `generate-echarts-image`, et `JSON.stringify`
   * supprime les fonctions SANS RIEN SIGNALER. Ce mode les remplace donc en
   * amont : sans lui, le service renvoie bien un PNG, mais l'infobulle et la
   * position sont perdues en silence. Même garde-fou que `buildPamiPolarOption`.
   */
  serializable?: boolean;
};

/**
 * Matching fit-culturel d'un candidat à son référentiel, en une seule figure —
 * même grammaire visuelle que `PamiMatchingChart`, adaptée à une échelle en
 * CRANS de pyramide (23 items, 7 niveaux).
 *
 * DEUX COUCHES SUPERPOSÉES, sur des géométries alignées :
 *
 * 1. Les **crans du référentiel**, en anneaux polaires d'UNE unité : un niveau
 *    attendu de 1 remplit `[0, 1]`, un niveau de 2 remplit `[1, 2]`, etc. La
 *    barre ne part pas du centre : c'est ce qui fait qu'« être au niveau
 *    attendu » se lit géométriquement — le point du candidat tombe dans
 *    l'anneau, ou il n'y tombe pas. Une barre partant du centre ne saurait pas
 *    dire qu'un niveau TROP HAUT s'écarte lui aussi de l'attendu, ce qui est
 *    exactement le cas dans un tri à distribution forcée.
 * 2. Le **profil du candidat**, en radar : polygone fermé sans remplissage,
 *    pour ne pas masquer les anneaux, plus un point par item coloré selon son
 *    statut. Aucune pondération en fit culturel : tous les points ont la même
 *    taille et les anneaux la même opacité.
 *
 * L'alignement des deux systèmes de coordonnées vient du même triplet que le
 * radar PAMI (`startAngle: 90`, sens trigonométrique, `boundaryGap: false`).
 * Sans lui, chaque point serait décalé d'un demi-secteur par rapport à son
 * propre cran — et la figure mentirait.
 */
export function buildFitCulturelMatchingOption(
  lignes: LigneMatchingFitCulturel[],
  {
    nbNiveaux,
    titre = "Radar de matching au référentiel",
    printMode = false,
    serializable = false,
  }: OptionMatchingParams,
): Record<string, unknown> {
  const categories = lignes.map((l) => l.label);
  /** Demi-largeur d'un secteur, en degrés : le décalage exact qui place un
   *  séparateur à mi-chemin entre deux items voisins. */
  const demiSecteur = categories.length > 0 ? 180 / categories.length : 0;
  // Résolue au calcul de l'option, donc relue à chaque re-rendu : un
  // changement de thème repeint les anneaux sans code supplémentaire.
  const couleurPrimary = couleurCran();
  const echelle = { min: 0, max: nbNiveaux };

  // À l'écran la figure fait 600 à 760 px ; l'image envoyée au document est
  // rendue plus grande (1100 px et plus) puis réduite à 120 mm. Tout ce qui est
  // écrit doit donc grossir dans la MÊME proportion, sinon les libellés
  // tombent sous les 4 points à l'impression.
  //
  // Le rapport « taille de police / côté du canevas » est ce qui fixe la mise
  // en page : l'augmenter davantage ferait se chevaucher les 23 étiquettes,
  // serrées près de l'axe vertical. Les valeurs ci-dessous gardent exactement
  // le rapport de l'écran (11 px pour ~760 px) — c'est la même figure, vue de
  // plus loin, pas une figure redessinée.
  //
  // Rayon agrandi à l'impression : `generate-echarts-image` rend sur un carré
  // de 1200 px, où la figure à 58 % laissait une large marge vide tandis que
  // les libellés du bas se touchaient (« Considération des personnes » contre
  // « Responsabilité sociale »). L'écart entre deux étiquettes voisines croît
  // avec le rayon, à police égale. 76 % est le plafond : au-delà, « Opportunités
  // de développement professionnel » sort à droite du canevas, et les libellés
  // écartés du bas en sortent par le dessous.
  const rayon = printMode ? "76%" : "62%";
  const centre = printMode ? ["50%", "50%"] : ["50%", "52%"];
  const policeNom = printMode ? 14 : 11;
  const interligneNom = printMode ? 17 : 13;
  const policeTitre = printMode ? 22 : 15;
  const taillePoint = printMode ? Math.round(TAILLE_POINT * 1.5) : TAILLE_POINT;
  const epaisseurProfil = printMode ? 3 : 2;

  const infobulle =
    printMode || serializable
      ? { show: false }
      : {
          trigger: "item",
          confine: true,
          // Décalée du curseur et non-survolable : posée SOUS le pointeur, la
          // boîte volait le survol à l'anneau, qui la faisait disparaître — d'où
          // un clignotement en boucle.
          enterable: false,
          position: (point: [number, number]) => [point[0] + 16, point[1] + 16],
          transitionDuration: 0,
          // Une seule infobulle, portée par les anneaux : elle dit TOUT ce qui
          // concerne l'item survolé.
          formatter: (params: { dataIndex: number }) => {
            const ligne = lignes[params.dataIndex];
            if (!ligne) return "";
            const ecart =
              ligne.ecart === 0
                ? "aucun"
                : `${ligne.ecart > 0 ? "+" : ""}${ligne.ecart} cran${
                    Math.abs(ligne.ecart) > 1 ? "s" : ""
                  }`;
            return [
              `<b>${ligne.label}</b>`,
              ligne.categorie ? `Dimension : ${ligne.categorie}` : null,
              `Niveau attendu : ${ligne.niveauEntreprise}/${nbNiveaux}`,
              `Niveau du candidat : ${ligne.niveauCandidat}/${nbNiveaux}`,
              `${LIBELLE_STATUT[ligne.statut]} · écart ${ecart}`,
            ]
              .filter(Boolean)
              .join("<br/>");
          },
        };

  return {
    animation: !printMode,
    title: {
      text: titre,
      left: "center",
      top: 8,
      textStyle: {
        fontSize: policeTitre,
        fontFamily: "sans-serif",
        fontWeight: 600,
      },
    },
    tooltip: infobulle,
    // Pas de légende : les anneaux partagent une teinte unique, il n'y a
    // aucune couleur à décoder. L'item est nommé sur son axe.
    legend: { show: false },
    toolbox: {
      show: !printMode,
      feature: { saveAsImage: { show: true } },
    },

    // ── Couche 1 : crans du référentiel ────────────────────────────────
    polar: [
      { radius: ["0%", rayon], center: centre },
      // Polaire jumeau, dédié aux SÉPARATEURS d'items. Il ne porte aucune
      // donnée : son seul rôle est de fournir des `splitLine` décalées d'un
      // demi-secteur, donc tracées ENTRE deux items et non au travers de l'un.
      { radius: ["0%", rayon], center: centre },
    ],
    angleAxis: [
      {
        type: "category",
        data: categories,
        // Collé à l'axe + sens trigonométrique + départ à 90° : c'est CE
        // triplet qui aligne les anneaux sur les sommets du radar.
        boundaryGap: false,
        clockwise: false,
        startAngle: 90,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },
      {
        polarIndex: 1,
        // Axe NUMÉRIQUE 0–360 et non catégoriel : un axe de catégories en
        // `boundaryGap: false` ne couvre que (N-1)/N du tour — il manque un
        // secteur, et tout ce que l'axe trace s'y interrompt.
        type: "value",
        min: 0,
        max: 360,
        interval: demiSecteur * 2,
        clockwise: false,
        startAngle: 90 + demiSecteur,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: "dashed", color: TRAIT_DIMENSION, width: 1 },
        },
        z: 1,
      },
    ],
    radiusAxis: [
      {
        type: "value",
        min: echelle.min,
        max: echelle.max,
        // Un cercle par cran : l'échelle EST discrète (7 niveaux), la grille
        // doit le dire.
        splitNumber: nbNiveaux,
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      {
        polarIndex: 1,
        type: "value",
        min: echelle.min,
        max: echelle.max,
        splitNumber: nbNiveaux,
        z: 1,
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "rgba(0,0,0,0.15)" },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
    ],

    // ── Couche 2 : radar du candidat ───────────────────────────────────
    radar: {
      center: centre,
      radius: rayon,
      startAngle: 90,
      shape: "polygon",
      splitNumber: nbNiveaux,
      indicator: lignes.map((l, index) => ({
        name: ecarterNom(
          couperNom(l.label, LARGEUR_NOM),
          index,
          lignes.length,
        ),
        min: echelle.min,
        max: echelle.max,
      })),
      // La grille est déjà tracée par le système polaire : la redoubler
      // rendrait la figure illisible. Le radar n'apporte que les noms.
      axisLine: { show: false },
      splitLine: { show: false },
      splitArea: { show: false },
      axisName: {
        fontSize: policeNom,
        fontFamily: "sans-serif",
        lineHeight: interligneNom,
        color: "#334155",
      },
    },

    series: [
      {
        // Série vide rattachée au polaire jumeau : ECharts ne rend un système
        // de coordonnées que s'il porte au moins une série.
        name: "separateurs",
        type: "scatter",
        coordinateSystem: "polar",
        polarIndex: 1,
        silent: true,
        symbolSize: 0,
        tooltip: { show: false },
        data: [],
        z: 1,
      },
      {
        // Socle invisible : porte la barre jusqu'au cran inférieur sans rien
        // dessiner. C'est ce qui transforme une barre en ANNEAU d'une unité.
        name: "socle",
        type: "bar",
        coordinateSystem: "polar",
        stack: "cran",
        silent: true,
        itemStyle: { color: "transparent" },
        tooltip: { show: false },
        data: lignes.map((l) => l.niveauEntreprise - 1),
        z: 2,
      },
      {
        name: "Niveau attendu",
        type: "bar",
        coordinateSystem: "polar",
        stack: "cran",
        barCategoryGap: "18%",
        itemStyle: {
          color: couleurPrimary,
          // Opacité UNIFORME : sans pondération, une intensité variable
          // ferait lire une importance qui n'existe pas.
          opacity: 0.5,
          borderColor: "#fff",
          borderWidth: 1,
        },
        // Le repeint d'`emphasis` ferait lire une autre intensité, donc une
        // autre importance. L'infobulle suffit à signaler le survol.
        emphasis: { disabled: true },
        // Toujours 1 : un cran attendu de 3 occupe `[2, 3]`.
        data: lignes.map(() => 1),
        z: 2,
      },
      {
        name: "Profil du candidat",
        type: "radar",
        symbol: "none",
        lineStyle: { width: epaisseurProfil, color: "#0f172a" },
        // Pas d'`areaStyle` : un remplissage masquerait les crans en dessous,
        // qui sont le référentiel lui-même.
        tooltip: { show: false },
        data: [{ value: lignes.map((l) => rayonDuNiveau(l.niveauCandidat)) }],
        z: 5,
      },
      {
        // Points en scatter POLAIRE et non en symboles du radar : seul le
        // scatter accepte une couleur par point, et c'est elle qui porte le
        // statut (au niveau / sous / au-dessus du cran attendu).
        name: "Niveau du candidat",
        type: "scatter",
        coordinateSystem: "polar",
        symbolSize: taillePoint,
        data: lignes.map((l, index) => ({
          value: [rayonDuNiveau(l.niveauCandidat), index],
          itemStyle: {
            color: COULEUR_STATUT[l.statut],
            borderColor: "#fff",
            borderWidth: 1.5,
          },
        })),
        z: 6,
      },
    ],
  };
}
